import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { useAnalyticsStream } from '@/contexts/AnalyticsStreamContext';
import { formatEventMessage } from '@/lib/EventContract';
import { MessageSquare, ThumbsUp, ThumbsDown, AlertCircle, RefreshCw } from 'lucide-react';

// Debounce time in ms - prevent showing same type of toast within this window
const TOAST_DEBOUNCE_MS = 5000;

// Country code to flag emoji mapping
const countryToFlag = (countryCode) => {
  if (!countryCode) return '🌍';
  
  const flags = {
    'US': '🇺🇸', 'GB': '🇬🇧', 'UK': '🇬🇧', 'CA': '🇨🇦', 'AU': '🇦🇺',
    'DE': '🇩🇪', 'FR': '🇫🇷', 'ES': '🇪🇸', 'IT': '🇮🇹', 'NL': '🇳🇱',
    'BR': '🇧🇷', 'MX': '🇲🇽', 'AR': '🇦🇷', 'CO': '🇨🇴', 'CL': '🇨🇱',
    'JP': '🇯🇵', 'CN': '🇨🇳', 'KR': '🇰🇷', 'IN': '🇮🇳', 'SG': '🇸🇬',
    'AE': '🇦🇪', 'SA': '🇸🇦', 'ZA': '🇿🇦', 'NG': '🇳🇬', 'EG': '🇪🇬',
    'PL': '🇵🇱', 'SE': '🇸🇪', 'NO': '🇳🇴', 'DK': '🇩🇰', 'FI': '🇫🇮',
    'BE': '🇧🇪', 'CH': '🇨🇭', 'AT': '🇦🇹', 'IE': '🇮🇪', 'PT': '🇵🇹',
    'NZ': '🇳🇿', 'PH': '🇵🇭', 'ID': '🇮🇩', 'MY': '🇲🇾', 'TH': '🇹🇭',
    'VN': '🇻🇳', 'TW': '🇹🇼', 'HK': '🇭🇰', 'IL': '🇮🇱', 'TR': '🇹🇷',
  };
  
  return flags[countryCode.toUpperCase()] || '🌍';
};

// Country code to name mapping
const countryToName = (countryCode) => {
  if (!countryCode) return 'Unknown';
  
  const names = {
    'US': 'United States', 'GB': 'United Kingdom', 'UK': 'United Kingdom',
    'CA': 'Canada', 'AU': 'Australia', 'DE': 'Germany', 'FR': 'France',
    'ES': 'Spain', 'IT': 'Italy', 'NL': 'Netherlands', 'BR': 'Brazil',
    'MX': 'Mexico', 'AR': 'Argentina', 'CO': 'Colombia', 'CL': 'Chile',
    'JP': 'Japan', 'CN': 'China', 'KR': 'South Korea', 'IN': 'India',
    'SG': 'Singapore', 'AE': 'UAE', 'SA': 'Saudi Arabia', 'ZA': 'South Africa',
  };
  
  return names[countryCode.toUpperCase()] || countryCode;
};

/**
 * Analytics Toast Provider
 * 
 * Listens to real-time analytics events and shows toast notifications.
 * Must be placed inside the app where useAuth is available.
 * 
 * NOTE: Test mode conversations are excluded from toast notifications
 * to prevent spam during Eva Content Tester usage.
 * 
 * Includes debouncing to prevent rapid-fire toasts of the same type.
 */
export function AnalyticsToastProvider({ children, enabled = true }) {
  // Get events from the shared analytics stream context
  const { events } = useAnalyticsStream();
  
  // Track last toast times to prevent spam
  const lastToastTimes = useRef({
    query: 0,
    feedback: 0,
    content_gap: 0,
    performance_update: 0
  });
  
  // Track last processed event index
  const lastEventIndexRef = useRef(0);

  const shouldShowToast = (type) => {
    const now = Date.now();
    const lastTime = lastToastTimes.current[type] || 0;
    
    if (now - lastTime < TOAST_DEBOUNCE_MS) {
      return false;
    }
    
    lastToastTimes.current[type] = now;
    return true;
  };

  // Process new events when they arrive
  useEffect(() => {
    if (events.length === 0) return;

    const handleEvent = (event) => {
      if (!enabled) return;

      // Skip test mode events to prevent notification spam
      if (event.data?.is_test || event.data?.session_type === 'test') {
        return;
      }

      switch (event.type) {
        case 'query':
        case 'retrieval':
          // Only show question toasts for real (non-test) conversations
          // and with debouncing
          if (!event.data?.is_test && shouldShowToast('query')) {
            showRetrievalToast(event.data);
          }
          break;
        case 'feedback':
          if (shouldShowToast('feedback')) {
            showFeedbackToast(event.data);
          }
          break;
        case 'content_gap':
          if (event.data?.is_new && shouldShowToast('content_gap')) {
            showContentGapToast(event.data);
          }
          break;
        case 'performance_update':
          if (shouldShowToast('performance_update')) {
            showPerformanceToast(event.data);
          }
          break;
        default:
          break;
      }
    };

    // Process only new events since last check
    const newEvents = events.slice(lastEventIndexRef.current);
    lastEventIndexRef.current = events.length;

    // Handle each new event
    newEvents.forEach(handleEvent);
  }, [events, enabled]);
  
  return children;
}

function showRetrievalToast(data) {
  const flag = countryToFlag(data?.country);
  const docCount = data?.document_count || 0;
  const headline = formatEventMessage({ type: 'query', data });
  
  toast(
    <div className="flex items-start gap-3">
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-info/10  flex items-center justify-center">
        <MessageSquare className="w-4 h-4 text-info " />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm text-foreground">{headline}</p>
        <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
          {flag} {docCount} document{docCount !== 1 ? 's' : ''} matched
        </p>
        <p className="text-xs text-info mt-1">
          Live query activity
        </p>
      </div>
    </div>,
    {
      duration: 5000,
      position: 'bottom-right',
    }
  );
}

function showFeedbackToast(data) {
  const isPositive = data?.feedback === 'positive';
  const Icon = isPositive ? ThumbsUp : ThumbsDown;
  const bgColor = isPositive 
    ? 'bg-success/10 ' 
    : 'bg-destructive/10 ';
  const iconColor = isPositive 
    ? 'text-success ' 
    : 'text-destructive ';
  const headline = formatEventMessage({ type: 'feedback', data });
  
  toast(
    <div className="flex items-start gap-3">
      <div className={`flex-shrink-0 w-8 h-8 rounded-full ${bgColor} flex items-center justify-center`}>
        <Icon className={`w-4 h-4 ${iconColor}`} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm text-foreground">
          {headline}
        </p>
        <p className="text-xs text-muted-foreground mt-0.5">
          Response feedback received
        </p>
      </div>
    </div>,
    {
      duration: 4000,
      position: 'bottom-right',
    }
  );
}

function showContentGapToast(data) {
  const headline = formatEventMessage({ type: 'content_gap', data });
  
  toast(
    <div className="flex items-start gap-3">
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-warning/10  flex items-center justify-center">
        <AlertCircle className="w-4 h-4 text-warning " />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm text-foreground">{headline}</p>
        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
          Missing knowledge base coverage
        </p>
        <p className="text-xs text-warning mt-1">
          No KB content matches this query
        </p>
      </div>
    </div>,
    {
      duration: 6000,
      position: 'bottom-right',
    }
  );
}

function showPerformanceToast(data) {
  const count = data?.documents_updated || 0;
  const headline = formatEventMessage({ type: 'performance_update', data });
  
  toast(
    <div className="flex items-start gap-3">
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10  flex items-center justify-center">
        <RefreshCw className="w-4 h-4 text-primary " />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm text-foreground">{headline}</p>
        <p className="text-xs text-muted-foreground mt-0.5">
          Performance metrics refreshed
        </p>
      </div>
    </div>,
    {
      duration: 3000,
      position: 'bottom-right',
    }
  );
}

export default AnalyticsToastProvider;
